import { useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getPatient, updatePatient } from '../../api/patients.js'
import { listEncounters, createEncounter } from '../../api/encounters.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { formatDate, formatDateTime, statusColor, dobAge } from '../../utils/format.js'

/**
 * Patient detail — demographics + encounter timeline + orders roll-up.
 *
 * This is the doctor's single-patient chart view. It pulls the patient record
 * and all encounters for that patient and aggregates the order surface so the
 * clinician can see at a glance what's in flight with the various providers.
 */
export default function PatientDetail() {
  const { patientId } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)

  const patientQ = useQuery({
    queryKey: ['patient', patientId],
    queryFn: () => getPatient(patientId),
    enabled: Boolean(patientId),
  })

  const encountersQ = useQuery({
    queryKey: ['encounters', { patient: patientId }],
    queryFn: () => listEncounters({ patient: patientId }),
    enabled: Boolean(patientId),
  })

  const createEnc = useMutation({
    mutationFn: () => createEncounter(patientId),
    onSuccess: (enc) => {
      qc.invalidateQueries({ queryKey: ['encounters'] })
      nav(`/doctor/encounters/${enc.id}`)
    },
  })

  const patient = patientQ.data
  const encounters = useMemo(() => encountersQ.data || [], [encountersQ.data])

  // Roll up all orders across this patient's encounters, most-recent first.
  const ordersRollup = useMemo(() => {
    const rows = []
    for (const e of encounters) {
      const when = e.submitted_at || e.updated_at || e.created_at
      for (const rx of e.prescriptions || []) {
        rows.push({ kind: 'Rx', id: rx.id, status: rx.status, when, encounterId: e.id, label: summarizeRx(rx) })
      }
      for (const lo of e.lab_orders || []) {
        rows.push({ kind: 'Lab', id: lo.id, status: lo.status, when, encounterId: e.id, label: summarizeLab(lo), result: lo.result_summary })
      }
      for (const io of e.imaging_orders || []) {
        rows.push({ kind: 'Imaging', id: io.id, status: io.status, when, encounterId: e.id, label: summarizeImg(io) })
      }
      for (const rf of e.referrals || []) {
        rows.push({ kind: 'Referral', id: rf.id, status: rf.status, when, encounterId: e.id, label: rf.specialty || rf.reason?.slice(0, 60) || 'Referral' })
      }
      for (const a of e.ambulance_requests || []) {
        rows.push({ kind: 'Ambulance', id: a.id, status: a.status, when, encounterId: e.id, label: `${a.pickup_address || ''} → ${a.destination || ''}` })
      }
    }
    rows.sort((a, b) => (b.when || '').localeCompare(a.when || ''))
    return rows
  }, [encounters])

  if (patientQ.isLoading) return <div className="p-4 text-sm text-slate-400">Loading patient…</div>
  if (patientQ.isError || !patient) {
    return (
      <div className="space-y-3">
        <ErrorBanner error={patientQ.error || new Error('Patient not found')} onRetry={() => patientQ.refetch()} />
        <Link to="/doctor" className="text-xs underline">Back to dashboard</Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Patient</div>
            <div className="font-semibold text-lg truncate">
              {patient.full_name || patient.name || patient.mrn}
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-1">
              {patient.mrn && (
                <span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">MRN</span>
                  <span className="font-mono">{patient.mrn}</span>
                </span>
              )}
              {patient.sex && <span className="capitalize">· {patient.sex}</span>}
              {patient.dob && <span>· {dobAge(patient.dob)}</span>}
              {patient.phone && <span>· {patient.phone}</span>}
              {patient.province && <span>· {patient.province}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/doctor" className="px-3 py-2 border rounded text-sm bg-white hover:bg-slate-50">Back</Link>
            <button
              onClick={() => setEditing(true)}
              className="px-3 py-2 border border-wantok-700 text-wantok-700 bg-white rounded text-sm hover:bg-wantok-50"
            >
              Edit details
            </button>
            <button
              onClick={() => createEnc.mutate()}
              disabled={createEnc.isPending}
              className="px-3 py-2 bg-wantok-700 hover:bg-wantok-800 text-white rounded text-sm disabled:opacity-60"
            >
              {createEnc.isPending ? 'Starting…' : 'New encounter'}
            </button>
          </div>
        </div>
        {createEnc.isError && <div className="mt-3"><ErrorBanner error={createEnc.error} /></div>}
      </Card>

      {/* Encounter history */}
      <Card>
        <CardHeader
          title="Encounter history"
          subtitle={encounters.length ? `${encounters.length} encounter(s)` : 'No encounters yet'}
          right={
            <button onClick={() => encountersQ.refetch()} className="text-xs text-wantok-700 underline">Refresh</button>
          }
        />
        {encountersQ.error && <ErrorBanner error={encountersQ.error} onRetry={() => encountersQ.refetch()} />}
        <ul className="divide-y divide-slate-100">
          {encountersQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
          {!encountersQ.isLoading && encounters.length === 0 && (
            <li className="py-3 text-sm text-slate-400">No prior encounters.</li>
          )}
          {encounters.map((e) => {
            const codes = (e.icd10_codes || []).slice(0, 3)
            const extras = (e.icd10_codes || []).length - codes.length
            return (
              <li key={e.id} className="py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm flex flex-wrap items-center gap-2">
                      <span className={`text-[10px] uppercase border rounded-full px-2 py-0.5 ${statusColor(e.status)}`}>{e.status}</span>
                      <span className="text-slate-700">
                        {formatDateTime(e.submitted_at || e.updated_at || e.created_at)}
                      </span>
                    </div>
                    {codes.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {codes.map((c) => (
                          <span key={c.code} className="text-[11px] bg-wantok-50 text-wantok-800 border border-wantok-100 rounded-full px-2 py-0.5">
                            <span className="font-mono mr-1">{c.code}</span>{c.description}
                          </span>
                        ))}
                        {extras > 0 && <span className="text-[11px] text-slate-400">+{extras} more</span>}
                      </div>
                    )}
                    {e.narrative && (
                      <div className="mt-1 text-xs text-slate-500 line-clamp-2 whitespace-pre-wrap">
                        {e.narrative}
                      </div>
                    )}
                  </div>
                  <Link to={`/doctor/encounters/${e.id}`} className="text-xs text-wantok-700 underline whitespace-nowrap">Open</Link>
                </div>
              </li>
            )
          })}
        </ul>
      </Card>

      {/* Orders roll-up */}
      {ordersRollup.length > 0 && (
        <Card>
          <CardHeader title="Orders & results" subtitle="Status across every encounter, most recent first" />
          <ul className="divide-y divide-slate-100">
            {ordersRollup.map((o) => (
              <li key={`${o.kind}-${o.id}`} className="py-2 flex items-start gap-2">
                <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 shrink-0 mt-0.5">{o.kind}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{o.label}</div>
                  {o.result && (
                    <div className="text-xs text-emerald-700 mt-0.5 line-clamp-2">Result: {o.result}</div>
                  )}
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(o.status)}`}>{o.status}</span>
                    <span>{formatDateTime(o.when)}</span>
                    <Link to={`/doctor/encounters/${o.encounterId}`} className="text-wantok-700 underline">encounter</Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {editing && (
        <EditPatientModal
          patient={patient}
          onClose={() => setEditing(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['patient', patientId] })
            qc.invalidateQueries({ queryKey: ['encounters'] })
            setEditing(false)
          }}
        />
      )}
    </div>
  )
}

// ---- helpers ---------------------------------------------------------------

function summarizeRx(rx) {
  const items = (rx.items || []).map((i) => i.drug_name || i.drug_code).filter(Boolean)
  if (items.length === 0) return 'Prescription'
  return items.join(', ')
}

function summarizeLab(lo) {
  const items = (lo.items || []).map((i) => i.test_name || i.test_code).filter(Boolean)
  if (items.length === 0) return 'Lab order'
  return items.join(', ')
}

function summarizeImg(io) {
  const items = (io.items || []).map((i) => i.study_name || i.study_code).filter(Boolean)
  if (items.length === 0) return 'Imaging'
  return items.join(', ')
}

// ---- Edit patient demographics --------------------------------------------
// Lets a doctor complete records imported without a DOB/sex (roster uploads)
// or correct any demographic. The backend PATCHes the PatientProfile; all
// fields are optional so a partial fix (e.g. just the DOB) is fine.

const _ic = 'w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white'

function EditField({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

function EditPatientModal({ patient, onClose, onSaved }) {
  // Name lives on the backing User row as first+last; the read API only
  // exposes the combined `full_name`. Best-effort split for pre-fill
  // (first token = first name, remainder = last name); the doctor sees
  // both fields and can correct. We compare against these originals on
  // save so an untouched name is never re-written.
  const _nameParts = String(patient.full_name || '').trim().split(/\s+/).filter(Boolean)
  const _initialFirst = _nameParts[0] || ''
  const _initialLast = _nameParts.slice(1).join(' ')
  const _initialEmail = patient.email || ''

  const [form, setForm] = useState({
    first_name: _initialFirst,
    last_name: _initialLast,
    email: _initialEmail,
    date_of_birth: patient.date_of_birth || '',
    sex: patient.sex || '',
    phone_number: patient.phone_number || patient.phone || '',
    place_of_residence: patient.place_of_residence || patient.province || '',
    emergency_contact_name: patient.emergency_contact_name || '',
    emergency_contact_phone: patient.emergency_contact_phone || '',
  })
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const mut = useMutation({
    mutationFn: () => {
      const patch = {
        date_of_birth: form.date_of_birth || null,
        sex: form.sex || '',
        phone_number: form.phone_number || '',
        place_of_residence: form.place_of_residence || '',
        emergency_contact_name: form.emergency_contact_name || '',
        emergency_contact_phone: form.emergency_contact_phone || '',
      }
      // Identity fields write through to the User row. Only send them when
      // actually changed — keeps the audit log clean and avoids re-splitting
      // an unedited name. Name is interdependent, so send both together.
      if (form.first_name.trim() !== _initialFirst || form.last_name.trim() !== _initialLast) {
        patch.first_name = form.first_name.trim()
        patch.last_name = form.last_name.trim()
      }
      if (form.email.trim() !== _initialEmail) {
        patch.email_in = form.email.trim()
      }
      return updatePatient(patient.id, patch)
    },
    onSuccess: onSaved,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-sand-200 w-full max-w-lg mt-12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">Edit patient details</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Close">×</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <EditField label="First name">
              <input value={form.first_name} onChange={set('first_name')} className={_ic} />
            </EditField>
            <EditField label="Last name">
              <input value={form.last_name} onChange={set('last_name')} className={_ic} />
            </EditField>
          </div>
          <EditField label="Email">
            <input type="email" value={form.email} onChange={set('email')} placeholder="patient@example.com" className={_ic} />
            <div className="text-[11px] text-slate-400 mt-1">Family members may share an email.</div>
          </EditField>
          <div className="grid grid-cols-2 gap-3">
            <EditField label="Date of birth">
              <input type="date" value={form.date_of_birth} onChange={set('date_of_birth')} className={_ic} />
            </EditField>
            <EditField label="Sex">
              <select value={form.sex} onChange={set('sex')} className={_ic}>
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </EditField>
          </div>
          <EditField label="Phone">
            <input value={form.phone_number} onChange={set('phone_number')} placeholder="+675 …" className={_ic} />
          </EditField>
          <EditField label="Residential address">
            <input value={form.place_of_residence} onChange={set('place_of_residence')} placeholder="e.g. Taurama, NCD" className={_ic} />
          </EditField>
          <div className="grid grid-cols-2 gap-3">
            <EditField label="Emergency contact — name">
              <input value={form.emergency_contact_name} onChange={set('emergency_contact_name')} className={_ic} />
            </EditField>
            <EditField label="Emergency contact — phone">
              <input value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} className={_ic} />
            </EditField>
          </div>
          {mut.error && <ErrorBanner error={mut.error} />}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={mut.isPending}
              className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50">
              Cancel
            </button>
            <button type="submit" disabled={mut.isPending}
              className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50">
              {mut.isPending ? 'Saving…' : 'Save details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
